import { useEffect, useRef, useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Play, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { PickerOption } from "@/lib/liveavatar/options";

export function PickerSelect({
  label, helper, value, onChange, options, loading, error,
  placeholder = "Select…", showPreview = false, previewFetcher,
}: {
  label: string; helper?: string; value?: string; onChange: (id: string) => void;
  options: PickerOption[]; loading?: boolean; error?: Error | null;
  placeholder?: string; showPreview?: boolean;
  previewFetcher?: (option: PickerOption) => Promise<{ audio_base64: string; mime: string }>;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [rowPlayingId, setRowPlayingId] = useState<string | null>(null);
  const [rowLoadingId, setRowLoadingId] = useState<string | null>(null);
  const selected = options.find((o) => o.id === value);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => setRowPlayingId(null);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onEnd);
    return () => {
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onEnd);
    };
  }, []);

  async function playRow(option: PickerOption) {
    if (!audioRef.current) return;
    if (rowPlayingId === option.id) {
      audioRef.current.pause();
      setRowPlayingId(null);
      return;
    }
    try {
      audioRef.current.pause();
      setRowLoadingId(option.id);
      if (previewFetcher) {
        const { audio_base64, mime } = await previewFetcher(option);
        audioRef.current.src = `data:${mime};base64,${audio_base64}`;
      } else if (option.preview_url) {
        audioRef.current.src = option.preview_url;
      } else {
        return;
      }
      await audioRef.current.play();
      setRowPlayingId(option.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Preview failed");
    } finally {
      setRowLoadingId(null);
    }
  }

  async function handlePreview() {
    if (!selected || !audioRef.current) return;
    try {
      if (previewFetcher) {
        setPreviewing(true);
        const { audio_base64, mime } = await previewFetcher(selected);
        audioRef.current.src = `data:${mime};base64,${audio_base64}`;
      } else if (selected.preview_url) {
        audioRef.current.src = selected.preview_url;
      } else { return; }
      await audioRef.current.play().catch(() => {});
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Preview failed");
    } finally { setPreviewing(false); }
  }

  const canPreview = showPreview && !!selected && (!!previewFetcher || !!selected.preview_url);

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
      <div className="flex items-center gap-2">
        <Select value={value || undefined} onValueChange={onChange} disabled={loading || !!error}>
          <SelectTrigger>
            <SelectValue
              placeholder={loading ? "Loading…" : error ? "Failed to load" : options.length === 0 ? "No options available" : placeholder}
            >
              {selected && (
                <div className="flex flex-1 min-w-0 items-center justify-between gap-3">
                  <span className="truncate min-w-0 flex-1 text-left">{selected.label}</span>
                  {selected.sublabel && (
                    <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                      {selected.sublabel}
                    </span>
                  )}
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <div key={o.id} className="relative">
                <SelectItem value={o.id} textValue={o.label} className={showPreview ? "pr-16" : undefined}>
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="truncate min-w-0 flex-1">{o.label}</span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{o.label}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {o.sublabel && (
                    <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                      {o.sublabel}
                    </span>
                  )}
                </SelectItem>
                {showPreview && (previewFetcher || o.preview_url) && (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={rowPlayingId === o.id ? "Pause preview" : "Play preview"}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); void playRow(o); }}
                    className="absolute right-7 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background hover:bg-accent z-10"
                  >
                    {rowLoadingId === o.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : rowPlayingId === o.id ? (
                      <Pause className="h-3 w-3" />
                    ) : (
                      <Play className="h-3 w-3" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </SelectContent>
        </Select>
        {canPreview && (
          <Button type="button" variant="outline" size="icon" disabled={previewing} onClick={handlePreview} title="Preview voice">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          </Button>
        )}
      </div>
      {showPreview && <audio ref={audioRef} className="hidden" />}
      {error && <p className="text-xs text-destructive">{error.message || "Failed to load options"}</p>}
    </div>
  );
}
